import { mergeRefs } from "@regrello/core-utils";
import React from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";
import { RegrelloFormFieldCheckbox, type RegrelloFormFieldCheckboxProps } from "../RegrelloFormFieldCheckbox";
import type { FormFieldPropsToOmit } from "./types";
import { getErrorProp, type RegrelloControlledFormFieldProps, useInvokeOnChangeOnRequiredStateChange } from "./utils";

/**
 * A controlled checkbox field for use with react-hook-form.
 */
function RegrelloControlledFormFieldCheckboxInternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>, // TODO: Change to PathToValueOfType<TFieldValues, boolean>
>({
  controllerProps,
  onValueChange,
  ...checkboxFieldProps
}: RegrelloControlledFormFieldProps<TFieldValues, TName, Omit<RegrelloFormFieldCheckboxProps, FormFieldPropsToOmit>> & {
  /**
   * Capture the change event before the rerender happens. This is useful for setting the
   * "useForm" form values to the correct type before rendering a new RegrelloFormField of a
   * different type. This avoids passing in a incorrect data type to the component.
   */
  onValueChange?: (name: string, newValue: boolean | null) => void;
}) {
  const { field, fieldState } = useController(controllerProps);
  const { ref, ...fieldProps } = field;

  // (max): This isn't really necessary as checkboxes are always optional, but including for consistency.
  useInvokeOnChangeOnRequiredStateChange(field, checkboxFieldProps.isRequiredAsteriskShown ?? false);

  return (
    <RegrelloFormFieldCheckbox
      {...fieldProps}
      {...checkboxFieldProps}
      {...getErrorProp(fieldState)}
      ref={checkboxFieldProps.ref != null ? mergeRefs(ref, checkboxFieldProps.ref) : ref}
      onChange={(newValue) => {
        onValueChange?.(field.name, newValue);
        fieldProps.onChange(newValue);
      }}
    />
  );
}

export const RegrelloControlledFormFieldCheckbox = React.memo(
  RegrelloControlledFormFieldCheckboxInternal,
) as typeof RegrelloControlledFormFieldCheckboxInternal;
